<script setup>
/* global axios */
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import PageHeader from '../SettingsSubPageHeader.vue';
import BaseButton from 'dashboard/components-next/button/Button.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

// State
const isLoading = ref(false);
const isSaving = ref(false);
const isPreviewing = ref(false);
const previewData = ref(null);

// Form data
const form = ref({
  name: '',
  keyword: '',
  inbox_id: null,
  description: '',
  alt_text: '',
  is_active: true,
  template_mode: 'single',
  template_json: {},
  rules_json: [],
});

// Custom JSON (for Single mode)
const customJsonText = ref('');
const jsonError = ref('');

// Time Rules (for Set mode)
const activeRuleIndex = ref(null);
const ruleJsonTexts = ref({});
const ruleErrors = ref({});

// Mode options - computed for i18n
const modeOptions = computed(() => [
  { value: 'single', label: t('FLEX_TEMPLATES.MODE_SINGLE') },
  { value: 'set', label: t('FLEX_TEMPLATES.MODE_SET') },
]);

// Check mode
const isSingleMode = computed(() => form.value.template_mode === 'single');
const isSetMode = computed(() => form.value.template_mode === 'set');

// Add a new time rule
const addRule = () => {
  const newRule = {
    start_time: '00:00',
    end_time: '23:59',
    alt_text: '',
    priority: form.value.rules_json.length + 1,
    enabled: true,
    is_fallback: false,
    template_json: {},
  };
  form.value.rules_json.push(newRule);
  const idx = form.value.rules_json.length - 1;
  ruleJsonTexts.value[idx] =
    '{\n  "type": "bubble",\n  "body": {\n    "type": "box",\n    "layout": "vertical",\n    "contents": []\n  }\n}';
  activeRuleIndex.value = idx;
};

// Remove a time rule
const removeRule = index => {
  form.value.rules_json.splice(index, 1);
  delete ruleJsonTexts.value[index];
  delete ruleErrors.value[index];
  // Re-index remaining rules
  const newJsonTexts = {};
  const newErrors = {};
  form.value.rules_json.forEach((_, i) => {
    if (ruleJsonTexts.value[i] !== undefined) {
      newJsonTexts[i] = ruleJsonTexts.value[i];
    }
    if (ruleErrors.value[i] !== undefined) {
      newErrors[i] = ruleErrors.value[i];
    }
  });
  ruleJsonTexts.value = newJsonTexts;
  ruleErrors.value = newErrors;
  if (activeRuleIndex.value >= form.value.rules_json.length) {
    activeRuleIndex.value =
      form.value.rules_json.length > 0
        ? form.value.rules_json.length - 1
        : null;
  }
};

// Validate rule JSON
const validateRuleJson = index => {
  const text = ruleJsonTexts.value[index];
  if (!text || !text.trim()) {
    ruleErrors.value[index] = 'JSON is required';
    return false;
  }
  try {
    const parsed = JSON.parse(text);
    if (!parsed.type || !['bubble', 'carousel'].includes(parsed.type)) {
      ruleErrors.value[index] = "Type must be 'bubble' or 'carousel'";
      return false;
    }
    form.value.rules_json[index].template_json = parsed;
    ruleErrors.value[index] = '';
    return true;
  } catch (e) {
    ruleErrors.value[index] = 'Invalid JSON format';
    return false;
  }
};

// Validate all rules
const validateAllRules = () => {
  if (!isSetMode.value) return true;
  if (form.value.rules_json.length === 0) {
    useAlert(t('FLEX_TEMPLATES.RULES_REQUIRED'));
    return false;
  }
  let allValid = true;
  form.value.rules_json.forEach((_, index) => {
    if (!validateRuleJson(index)) {
      allValid = false;
    }
  });
  return allValid;
};

const inboxes = useMapGetter('inboxes/getInboxes');

// Use route.params.accountId directly to avoid timing issues with Vuex store sync
const accountId = computed(() => route.params.accountId);

const isEditing = computed(() => !!route.params.templateId);

const lineInboxes = computed(() => {
  return (
    inboxes.value?.filter(inbox => inbox.channel_type === 'Channel::Line') || []
  );
});

const inboxOptions = computed(() => {
  return [
    { value: null, label: t('FLEX_TEMPLATES.ACCOUNT_WIDE') },
    ...lineInboxes.value.map(inbox => ({
      value: inbox.id,
      label: inbox.name,
    })),
  ];
});

// Reset form to default values (for New mode or when switching from Edit to New)
const resetForm = () => {
  form.value = {
    name: '',
    keyword: '',
    inbox_id: null,
    description: '',
    alt_text: '',
    is_active: true,
    template_mode: 'single',
    template_json: {},
    rules_json: [],
  };
  customJsonText.value = '';
  jsonError.value = '';
  activeRuleIndex.value = null;
  ruleJsonTexts.value = {};
  ruleErrors.value = {};
  previewData.value = null;
};

onMounted(async () => {
  await store.dispatch('inboxes/get');

  if (isEditing.value) {
    await fetchTemplate();
  } else {
    // Reset form for New mode
    resetForm();
  }
});

// Watch for route changes to handle navigation between Edit and New modes
watch(
  () => route.params.templateId,
  async newTemplateId => {
    if (newTemplateId) {
      // Switched to Edit mode
      await fetchTemplate();
    } else {
      // Switched to New mode
      resetForm();
    }
  }
);

const fetchTemplate = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/flex_templates/${route.params.templateId}`
    );
    const data = response.data;
    form.value = {
      name: data.name,
      keyword: data.keyword || '',
      inbox_id: data.inbox_id,
      description: data.description || '',
      alt_text: data.alt_text || '',
      is_active: data.is_active,
      template_mode: data.template_mode || 'single',
      template_json: data.template_json || {},
      rules_json: data.rules_json || [],
    };

    // For Single mode
    customJsonText.value = JSON.stringify(data.template_json, null, 2);

    // For Set mode - populate rule JSON texts
    if (data.rules_json && data.rules_json.length > 0) {
      data.rules_json.forEach((rule, index) => {
        ruleJsonTexts.value[index] = JSON.stringify(
          rule.template_json || {},
          null,
          2
        );
      });
    }
  } catch (error) {
    console.error('[FlexTemplateEditor] Failed to fetch template:', error);
    console.error(
      '[FlexTemplateEditor] URL was:',
      `/api/v1/accounts/${accountId.value}/flex_templates/${route.params.templateId}`
    );
    useAlert(t('FLEX_TEMPLATES.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const validateJson = () => {
  if (!customJsonText.value.trim()) {
    jsonError.value = t('FLEX_TEMPLATES.JSON_REQUIRED');
    return false;
  }

  try {
    const parsed = JSON.parse(customJsonText.value);
    if (!parsed.type || !['bubble', 'carousel'].includes(parsed.type)) {
      jsonError.value = t('FLEX_TEMPLATES.INVALID_FLEX_TYPE');
      return false;
    }
    form.value.template_json = parsed;
    jsonError.value = '';
    return true;
  } catch (e) {
    jsonError.value = t('FLEX_TEMPLATES.INVALID_JSON');
    return false;
  }
};

const previewTemplate = async () => {
  if (!validateJson()) {
    useAlert(jsonError.value || t('FLEX_TEMPLATES.INVALID_JSON'));
    return;
  }

  isPreviewing.value = true;
  try {
    const response = await axios.post(
      `/api/v1/accounts/${accountId.value}/flex_templates/preview`,
      {
        template_json: form.value.template_json,
        alt_text: form.value.alt_text || form.value.name,
      }
    );
    previewData.value = response.data;
    useAlert(t('FLEX_TEMPLATES.PREVIEW_VALID'));
  } catch (error) {
    const errorMsg =
      error.response?.data?.error || t('FLEX_TEMPLATES.PREVIEW_ERROR');
    useAlert(errorMsg);
  } finally {
    isPreviewing.value = false;
  }
};

const saveTemplate = async () => {
  if (!form.value.name) {
    useAlert(t('FLEX_TEMPLATES.NAME_REQUIRED'));
    return;
  }

  // Validate based on mode
  if (isSingleMode.value) {
    if (!validateJson()) {
      useAlert(jsonError.value || t('FLEX_TEMPLATES.INVALID_JSON'));
      return;
    }
  } else if (!validateAllRules()) {
    return;
  }

  isSaving.value = true;
  try {
    const payload = {
      flex_template: {
        name: form.value.name,
        keyword: form.value.keyword,
        inbox_id: form.value.inbox_id,
        description: form.value.description,
        alt_text: form.value.alt_text,
        is_active: form.value.is_active,
        template_mode: form.value.template_mode,
        template_type: 'custom',
      },
    };

    // Add mode-specific data
    if (isSingleMode.value) {
      payload.flex_template.template_json = form.value.template_json;
      payload.flex_template.rules_json = [];
    } else {
      payload.flex_template.template_json = {}; // Empty for Set mode
      payload.flex_template.rules_json = form.value.rules_json;
    }

    const url = isEditing.value
      ? `/api/v1/accounts/${accountId.value}/flex_templates/${route.params.templateId}`
      : `/api/v1/accounts/${accountId.value}/flex_templates`;

    if (isEditing.value) {
      await axios.put(url, payload);
    } else {
      await axios.post(url, payload);
    }

    useAlert(
      isEditing.value
        ? t('FLEX_TEMPLATES.UPDATED')
        : t('FLEX_TEMPLATES.CREATED')
    );
    router.push({ name: 'flex_templates_list' });
  } catch (error) {
    const errorMsg =
      error.response?.data?.errors?.join(', ') ||
      t('FLEX_TEMPLATES.SAVE_ERROR');
    useAlert(errorMsg);
  } finally {
    isSaving.value = false;
  }
};

const cancel = () => {
  router.push({ name: 'flex_templates_list' });
};
</script>

<template>
  <div class="flex-editor-page h-full w-full p-6 col-span-6 overflow-auto">
    <BackButton
      :back-url="{ name: 'flex_templates_list' }"
      :button-label="t('FLEX_TEMPLATES.BACK_TO_LIST')"
      class="mb-4"
    />
    <PageHeader
      :header-title="
        isEditing
          ? t('FLEX_TEMPLATES.EDIT_TITLE')
          : t('FLEX_TEMPLATES.ADD_TITLE')
      "
      :header-content="t('FLEX_TEMPLATES.DESCRIPTION')"
    />

    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="text-n-slate-11">{{ t('FLEX_TEMPLATES.LOADING') }}</span>
    </div>

    <div v-else class="max-w-4xl space-y-6 mt-6">
      <!-- Basic Info -->
      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('FLEX_TEMPLATES.NAME') }} *
          </label>
          <input
            v-model="form.name"
            type="text"
            class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="t('FLEX_TEMPLATES.NAME_PLACEHOLDER')"
          />
        </div>
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('FLEX_TEMPLATES.KEYWORD') }} *
          </label>
          <input
            v-model="form.keyword"
            type="text"
            class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="t('FLEX_TEMPLATES.KEYWORD_PLACEHOLDER')"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('FLEX_TEMPLATES.INBOX') }}
          </label>
          <select
            v-model="form.inbox_id"
            class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
          >
            <option
              v-for="option in inboxOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('FLEX_TEMPLATES.ALT_TEXT') }}
          </label>
          <input
            v-model="form.alt_text"
            type="text"
            class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
            :placeholder="t('FLEX_TEMPLATES.ALT_TEXT_PLACEHOLDER')"
          />
        </div>
      </div>

      <div class="space-y-2">
        <label class="block text-sm font-medium text-n-slate-12">
          {{ t('FLEX_TEMPLATES.DESCRIPTION_LABEL') }}
        </label>
        <textarea
          v-model="form.description"
          rows="2"
          class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
          :placeholder="t('FLEX_TEMPLATES.DESCRIPTION_PLACEHOLDER')"
        />
      </div>

      <!-- Template Mode Selector -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-n-slate-12">
          {{ t('FLEX_TEMPLATES.MODE_LABEL') }} *
        </label>
        <div class="flex gap-4">
          <label
            v-for="option in modeOptions"
            :key="option.value"
            class="flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-all"
            :class="
              form.template_mode === option.value
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                : 'border-n-weak hover:border-n-strong'
            "
          >
            <input
              v-model="form.template_mode"
              type="radio"
              :value="option.value"
              class="w-4 h-4 text-woot-500"
            />
            <span class="text-sm font-medium text-n-slate-12">{{
              option.label
            }}</span>
          </label>
        </div>
        <p class="text-xs text-n-slate-11">
          <span v-if="isSingleMode">{{
            t('FLEX_TEMPLATES.MODE_SINGLE_DESC')
          }}</span>
          <span v-else>{{ t('FLEX_TEMPLATES.MODE_SET_DESC') }}</span>
        </p>
      </div>

      <!-- Flex JSON Editor (Single Mode) -->
      <div v-if="isSingleMode" class="space-y-4">
        <div class="flex items-center justify-between">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('FLEX_TEMPLATES.JSON_EDITOR') }} *
          </label>
          <BaseButton
            size="sm"
            variant="ghost"
            :loading="isPreviewing"
            @click="previewTemplate"
          >
            <span class="i-lucide-eye mr-1" />
            {{ t('FLEX_TEMPLATES.PREVIEW') }}
          </BaseButton>
        </div>

        <textarea
          v-model="customJsonText"
          class="w-full h-80 px-3 py-2 font-mono text-sm border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
          :placeholder="t('FLEX_TEMPLATES.JSON_PLACEHOLDER')"
          @blur="validateJson"
        />
        <p v-if="jsonError" class="text-sm text-r-500">{{ jsonError }}</p>

        <!-- Preview Result -->
        <div
          v-if="previewData?.valid"
          class="p-4 bg-g-50 dark:bg-g-900 border border-g-200 dark:border-g-800 rounded-lg"
        >
          <div class="flex items-center gap-2 text-g-700 dark:text-g-300">
            <span class="i-lucide-check-circle" />
            <span class="font-medium">{{
              t('FLEX_TEMPLATES.PREVIEW_VALID')
            }}</span>
          </div>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('FLEX_TEMPLATES.PREVIEW_INFO') }}
          </p>
        </div>

        <!-- Tips -->
        <div class="p-4 bg-n-alpha-1 rounded-lg">
          <h4 class="font-medium mb-2">{{ t('FLEX_TEMPLATES.JSON_TIPS') }}</h4>
          <ul class="text-sm text-n-slate-11 space-y-1 list-disc pl-4">
            <li>{{ t('FLEX_TEMPLATES.TIP_1') }}</li>
            <li>{{ t('FLEX_TEMPLATES.TIP_2') }}</li>
            <li>{{ t('FLEX_TEMPLATES.TIP_3') }}</li>
          </ul>
          <a
            href="https://developers.line.biz/flex-simulator/"
            target="_blank"
            class="inline-flex items-center gap-1 mt-3 text-sm font-medium text-woot-500 hover:underline"
          >
            <span class="i-lucide-external-link" />
            {{ t('FLEX_TEMPLATES.OPEN_SIMULATOR') }}
          </a>
        </div>
      </div>

      <!-- Time Rules Editor (Set Mode) -->
      <div v-if="isSetMode" class="space-y-4">
        <div class="flex items-center justify-between">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ t('FLEX_TEMPLATES.TIME_RULES') }} *
          </label>
          <BaseButton size="sm" variant="ghost" @click="addRule">
            <span class="i-lucide-plus mr-1" />
            {{ t('FLEX_TEMPLATES.ADD_RULE') }}
          </BaseButton>
        </div>

        <!-- Rules List -->
        <div
          v-if="form.rules_json.length === 0"
          class="p-8 border-2 border-dashed border-n-weak rounded-lg text-center"
        >
          <span class="i-lucide-clock text-4xl text-n-slate-9 mb-2" />
          <p class="text-n-slate-11">{{ t('FLEX_TEMPLATES.NO_RULES') }}</p>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('FLEX_TEMPLATES.NO_RULES_DESC') }}
          </p>
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="(rule, index) in form.rules_json"
            :key="index"
            class="border rounded-lg overflow-hidden"
            :class="
              activeRuleIndex === index ? 'border-woot-500' : 'border-n-weak'
            "
          >
            <!-- Rule Header -->
            <div
              class="flex items-center justify-between px-4 py-3 cursor-pointer"
              :class="
                activeRuleIndex === index
                  ? 'bg-woot-50 dark:bg-woot-900/20'
                  : 'bg-n-alpha-1'
              "
              @click="
                activeRuleIndex = activeRuleIndex === index ? null : index
              "
            >
              <div class="flex items-center gap-3">
                <span class="i-lucide-clock text-n-slate-10" />
                <span class="font-medium text-n-slate-12">
                  {{
                    rule.is_fallback
                      ? t('FLEX_TEMPLATES.FALLBACK_LABEL')
                      : `${rule.start_time} - ${rule.end_time}`
                  }}
                </span>
                <span
                  v-if="!rule.enabled"
                  class="px-2 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-10"
                >
                  {{ t('FLEX_TEMPLATES.DISABLED_LABEL') }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="i-lucide-chevron-down transition-transform"
                  :class="activeRuleIndex === index ? 'rotate-180' : ''"
                />
                <button
                  type="button"
                  class="p-1 text-r-500 hover:bg-r-50 dark:hover:bg-r-900/20 rounded"
                  @click.stop="removeRule(index)"
                >
                  <span class="i-lucide-trash-2" />
                </button>
              </div>
            </div>

            <!-- Rule Details (Expanded) -->
            <div
              v-if="activeRuleIndex === index"
              class="p-4 space-y-4 border-t border-n-weak"
            >
              <!-- Time Range -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">{{
                    t('FLEX_TEMPLATES.START_TIME')
                  }}</label>
                  <input
                    v-model="rule.start_time"
                    type="time"
                    :disabled="rule.is_fallback"
                    class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">{{
                    t('FLEX_TEMPLATES.END_TIME')
                  }}</label>
                  <input
                    v-model="rule.end_time"
                    type="time"
                    :disabled="rule.is_fallback"
                    class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none disabled:opacity-50"
                  />
                </div>
              </div>

              <p v-if="!rule.is_fallback" class="text-xs text-n-slate-10">
                💡 {{ t('FLEX_TEMPLATES.CROSS_MIDNIGHT_TIP') }}
              </p>

              <!-- Options -->
              <div class="flex flex-wrap gap-4">
                <label class="flex items-center gap-2">
                  <input
                    v-model="rule.enabled"
                    type="checkbox"
                    class="w-4 h-4"
                  />
                  <span class="text-sm text-n-slate-12">{{
                    t('FLEX_TEMPLATES.ENABLED')
                  }}</span>
                </label>
                <label class="flex items-center gap-2">
                  <input
                    v-model="rule.is_fallback"
                    type="checkbox"
                    class="w-4 h-4"
                  />
                  <span class="text-sm text-n-slate-12">{{
                    t('FLEX_TEMPLATES.USE_AS_FALLBACK')
                  }}</span>
                </label>
              </div>

              <!-- Alt Text -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">{{
                  t('FLEX_TEMPLATES.RULE_ALT_TEXT')
                }}</label>
                <input
                  v-model="rule.alt_text"
                  type="text"
                  class="w-full px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
                  :placeholder="t('FLEX_TEMPLATES.RULE_ALT_TEXT_PLACEHOLDER')"
                />
              </div>

              <!-- Priority -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12"
                  >{{ t('FLEX_TEMPLATES.PRIORITY') }} ({{
                    t('FLEX_TEMPLATES.PRIORITY_HINT')
                  }})</label
                >
                <input
                  v-model.number="rule.priority"
                  type="number"
                  min="1"
                  class="w-24 px-3 py-2 border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
                />
              </div>

              <!-- Flex JSON -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12"
                  >{{ t('FLEX_TEMPLATES.RULE_JSON') }} *</label
                >
                <textarea
                  v-model="ruleJsonTexts[index]"
                  class="w-full h-48 px-3 py-2 font-mono text-sm border rounded-lg bg-white dark:bg-n-solid-2 border-n-weak focus:border-woot-500 focus:outline-none"
                  placeholder='{"type": "bubble", "body": {...}}'
                  @blur="validateRuleJson(index)"
                />
                <p v-if="ruleErrors[index]" class="text-sm text-r-500">
                  {{ ruleErrors[index] }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tips for Set Mode -->
        <div class="p-4 bg-n-alpha-1 rounded-lg">
          <h4 class="font-medium mb-2">
            💡 {{ t('FLEX_TEMPLATES.SET_TIPS_TITLE') }}
          </h4>
          <ul class="text-sm text-n-slate-11 space-y-1 list-disc pl-4">
            <li>{{ t('FLEX_TEMPLATES.SET_TIP_1') }}</li>
            <li>{{ t('FLEX_TEMPLATES.SET_TIP_2') }}</li>
            <li>{{ t('FLEX_TEMPLATES.SET_TIP_3') }}</li>
            <li>{{ t('FLEX_TEMPLATES.SET_TIP_4') }}</li>
          </ul>
          <a
            href="https://developers.line.biz/flex-simulator/"
            target="_blank"
            class="inline-flex items-center gap-1 mt-3 text-sm font-medium text-woot-500 hover:underline"
          >
            <span class="i-lucide-external-link" />
            {{ t('FLEX_TEMPLATES.OPEN_SIMULATOR') }}
          </a>
        </div>
      </div>

      <!-- Active toggle -->
      <div class="flex items-center gap-2">
        <input
          id="is_active"
          v-model="form.is_active"
          type="checkbox"
          class="w-4 h-4"
        />
        <label for="is_active">{{ t('FLEX_TEMPLATES.IS_ACTIVE') }}</label>
      </div>

      <!-- Actions -->
      <div class="flex gap-4 pt-4" style="border-top: 1px solid #e5e7eb">
        <button class="save-btn" :disabled="isSaving" @click="saveTemplate">
          {{
            isSaving
              ? t('FLEX_TEMPLATES.SAVING')
              : isEditing
                ? t('FLEX_TEMPLATES.UPDATE')
                : t('FLEX_TEMPLATES.SAVE')
          }}
        </button>
        <button class="cancel-btn" @click="cancel">
          {{ t('FLEX_TEMPLATES.CANCEL') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.flex-editor-page,
.dark .flex-editor-page {
  background: #ffffff !important;
}

.save-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
}
.save-btn:hover {
  background: #1f2937;
}
.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cancel-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
}
.cancel-btn:hover {
  background: #f9fafb;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.bg-n-solid-2),
:deep(.bg-n-background),
:deep(.dark\:bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2) {
  background: #f9fafb !important;
}
:deep(input),
:deep(select),
:deep(textarea) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
:deep(label) {
  color: #111827 !important;
}
</style>
